/**
 * 解析曲面重合配合的公共几何工具。
 *
 * 文件职责：
 * 1. 提供 Coincident Mate 中解析曲面语义需要的实体分类与距离计算；
 * 2. 作为求解器与残差校验器的公共依赖，避免两处重复维护几何公式；
 * 3. 只消费可序列化 MateEntityRef 和 Three.js 数学类型，不接触场景对象或 UI 状态。
 */

import * as THREE from 'three';
import {
  MateAlignment,
  MateEntityKind,
  type MateEntityRef,
} from '../../definition';
import type {
  AxisGeometry,
  ConeGeometry,
} from './StandardMateGeometryAccess';

/** 解析曲面重合使用的默认长度容差。 */
export const SURFACE_COINCIDENT_EPSILON = 1e-9;

/**
 * 判断实体是否为圆柱面。
 *
 * @param entity 配合实体。
 * @returns 是否为圆柱面。
 */
export function isCylinderEntity(entity: MateEntityRef): boolean {
  return entity.entityKind === MateEntityKind.Cylinder;
}

/**
 * 判断实体是否为圆锥面。
 *
 * @param entity 配合实体。
 * @returns 是否为圆锥面。
 */
export function isConeEntity(entity: MateEntityRef): boolean {
  return entity.entityKind === MateEntityKind.Cone;
}

/**
 * 判断实体是否为球面。
 *
 * @param entity 配合实体。
 * @returns 是否为球面。
 */
export function isSphereEntity(entity: MateEntityRef): boolean {
  return entity.entityKind === MateEntityKind.Sphere;
}

/**
 * 判断实体是否为圆边。
 *
 * @param entity 配合实体。
 * @returns 是否为圆边。
 */
export function isCircularEdgeEntity(entity: MateEntityRef): boolean {
  return entity.entityKind === MateEntityKind.CircularEdge;
}

/**
 * 判断实体是否为线性轴实体。
 *
 * @param entity 配合实体。
 * @returns 是否为直线或基准轴。
 */
export function isLinearAxisEntity(entity: MateEntityRef): boolean {
  return entity.entityKind === MateEntityKind.Line
    || entity.entityKind === MateEntityKind.ReferenceAxis;
}

/**
 * 读取轴线半径。
 *
 * @param axis 轴线几何。
 * @returns 有效半径；缺失时返回 null。
 */
export function resolveAxisRadius(axis: AxisGeometry): number | null {
  return typeof axis.radius === 'number'
    && Number.isFinite(axis.radius)
    && axis.radius > SURFACE_COINCIDENT_EPSILON
    ? axis.radius
    : null;
}

/**
 * 计算点到轴线的投影点。
 *
 * @param point 待投影点。
 * @param axis 目标轴线。
 * @returns 轴线上的投影点。
 */
export function projectPointToAxis(point: THREE.Vector3, axis: AxisGeometry): THREE.Vector3 {
  const originToPoint = point.clone().sub(axis.origin);
  return axis.origin.clone().addScaledVector(axis.direction, originToPoint.dot(axis.direction));
}

/**
 * 计算点到轴线的最短距离。
 *
 * @param point 点。
 * @param axis 轴线。
 * @returns 最短距离。
 */
export function calculatePointAxisDistance(point: THREE.Vector3, axis: AxisGeometry): number {
  return point.distanceTo(projectPointToAxis(point, axis));
}

/**
 * 计算两条轴线的最短距离。
 *
 * @param first 第一条轴线。
 * @param second 第二条轴线。
 * @param epsilon 容差。
 * @returns 最短距离。
 */
export function calculateAxisAxisDistance(
  first: AxisGeometry,
  second: AxisGeometry,
  epsilon: number,
): number {
  const cross = first.direction.clone().cross(second.direction);
  const delta = second.origin.clone().sub(first.origin);
  if (cross.lengthSq() <= epsilon * epsilon) {
    return delta.sub(first.direction.clone().multiplyScalar(delta.dot(first.direction))).length();
  }
  return Math.abs(delta.dot(cross.normalize()));
}

/**
 * 判断方向是否满足配合对齐。
 *
 * @param first 第一方向。
 * @param second 第二方向。
 * @param alignment 配合对齐方式。
 * @param angleEpsilon 角度容差。
 * @returns 是否满足对齐。
 */
export function isDirectionAligned(
  first: THREE.Vector3,
  second: THREE.Vector3,
  alignment: MateAlignment | undefined,
  angleEpsilon: number,
): boolean {
  const target = alignment === MateAlignment.AntiAligned
    ? second.clone().multiplyScalar(-1)
    : second;
  return first.angleTo(target) <= angleEpsilon;
}

/**
 * 读取圆锥指定圆边半径对应的轴向距离。
 *
 * @param cone 圆锥几何。
 * @param edgeRadius 圆边半径。
 * @returns 圆边中心到圆锥顶点的轴向距离。
 */
export function resolveConeAxialDistanceForEdgeRadius(
  cone: ConeGeometry,
  edgeRadius: number,
): number | null {
  const tangent = Math.tan(cone.halfAngle);
  if (!Number.isFinite(tangent) || tangent <= SURFACE_COINCIDENT_EPSILON) {
    return null;
  }
  return edgeRadius / tangent;
}

/**
 * 校验点是否落在圆锥侧面上。
 *
 * @param point 点。
 * @param cone 圆锥几何。
 * @param epsilon 容差。
 * @returns 是否满足圆锥侧面重合。
 */
export function validatePointOnCone(
  point: THREE.Vector3,
  cone: ConeGeometry,
  epsilon: number,
): boolean {
  const apexToPoint = point.clone().sub(cone.apex);
  const axial = apexToPoint.dot(cone.direction);
  if (axial < -epsilon) {
    return false;
  }
  const radial = apexToPoint.sub(cone.direction.clone().multiplyScalar(axial)).length();
  return Math.abs(radial - axial * Math.tan(cone.halfAngle)) <= epsilon;
}
